import { useMemo, useState, type FormEvent } from 'react';
import type { AccountDto } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import Hint from './Hint';

interface Props {
  companyId: string;
  accounts: readonly AccountDto[];
  /** المحاسب والمالك (`journal.manual`) — غيرهم يرى الدليل للقراءة فقط. */
  canManage: boolean;
  onChanged: () => void;
  onOpenLedger: (accountCode: string) => void;
  onSessionLost: () => void;
}

const ISSUE_MESSAGES: Record<string, string> = {
  ACCOUNT_BALANCE_NOT_ZERO: 'الحساب عليه رصيد — انقل رصيده لحساب تانى بقيد الأول، وبعدين أوقفه.',
  ACCOUNT_LINKED_TO_TREASURY: 'الحساب مربوط بخزينة أو بنك شغال — أوقف الخزينة/البنك الأول.',
  SYSTEM_ACCOUNT:
    'ده من حسابات الدليل الأساسى اللى البرنامج بيعتمد عليها — ممكن توقفه بس مايتمسحش.',
  GROUP_ACCOUNT: 'ده حساب تجميعى — أوقف الحسابات اللى تحته بدل منه.',
  IN_USE: 'الحساب عليه قيود أو مربوط بحاجة — أوقفه بدل المسح.',
  TAX_ACCOUNT_RESERVED: 'حسابات الضرائب بتتعمل مع إعدادات الضريبة، مش من هنا.',
  PARENT_ACCOUNT_UNAVAILABLE: 'الحساب الأب لازم يكون حساب تجميعى نشط.',
  ACCOUNT_CODE_RANGE_EXHAUSTED: 'مفيش أكواد فاضية تحت الحساب ده.',
};

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const issues = caught.problem.issues ?? [];
    if (issues.length) return issues.map((i) => ISSUE_MESSAGES[i.code] ?? i.message).join(' ');
    return caught.problem.detail ?? caught.problem.title;
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/** عمق الحساب فى الشجرة — للمسافة البادئة. الأكواد بنفس الطول، فالعمق من سلسلة الآباء. */
function depths(accounts: readonly AccountDto[]): Map<string, number> {
  const parentOf = new Map(accounts.map((account) => [account.code, account.parentCode]));
  const result = new Map<string, number>();
  for (const account of accounts) {
    let depth = 0;
    let parent = account.parentCode;
    while (parent && depth < 12) {
      depth += 1;
      parent = parentOf.get(parent) ?? null;
    }
    result.set(account.code, depth);
  }
  return result;
}

/** ترتيب الشجرة: كل أب ثم أبناؤه، بالكود داخل كل مستوى. */
function treeOrder(accounts: readonly AccountDto[]): AccountDto[] {
  const children = new Map<string | null, AccountDto[]>();
  for (const account of accounts) {
    const list = children.get(account.parentCode) ?? [];
    list.push(account);
    children.set(account.parentCode, list);
  }
  const ordered: AccountDto[] = [];
  const walk = (parent: string | null) => {
    for (const account of (children.get(parent) ?? []).sort((a, b) =>
      a.code.localeCompare(b.code),
    )) {
      ordered.push(account);
      walk(account.code);
    }
  };
  walk(null);
  return ordered;
}

/**
 * دليل الحسابات: الشجرة كاملة، مع إضافة حساب فرعى تحت أى حساب تجميعى، وتعديل الاسم،
 * وإيقاف حساب غير مستخدم أو مسح حساب مُضاف بالخطأ. الكود والتصنيف لا يتغيران.
 */
export default function ChartOfAccounts({
  companyId,
  accounts,
  canManage,
  onChanged,
  onOpenLedger,
  onSessionLost,
}: Props) {
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState<{ parentCode: string; nameAr: string } | null>(null);
  const [renaming, setRenaming] = useState<{ code: string; nameAr: string } | null>(null);

  const depthOf = useMemo(() => depths(accounts), [accounts]);
  const rows = useMemo(() => {
    const needle = query.trim();
    return treeOrder(accounts).filter(
      (account) =>
        (showInactive || account.status === 'active') &&
        (!needle || account.code.includes(needle) || account.nameAr.includes(needle)),
    );
  }, [accounts, query, showInactive]);

  async function run(action: () => Promise<unknown>, done: string): Promise<void> {
    setBusy(true);
    setFailure(null);
    setNotice(null);
    try {
      await action();
      setNotice(done);
      onChanged();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setFailure(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  function submitAdd(event: FormEvent): void {
    event.preventDefault();
    if (!adding || adding.nameAr.trim().length < 2) return;
    const body = { parentCode: adding.parentCode, nameAr: adding.nameAr.trim() };
    void run(async () => {
      const created = await api.createAccount(companyId, body);
      setAdding(null);
      return created;
    }, 'اتضاف الحساب ' + body.nameAr);
  }

  function submitRename(event: FormEvent): void {
    event.preventDefault();
    if (!renaming || renaming.nameAr.trim().length < 2) return;
    const target = renaming;
    void run(async () => {
      await api.updateAccount(companyId, target.code, { nameAr: target.nameAr.trim() });
      setRenaming(null);
    }, 'اتعدّل اسم الحساب ' + target.code);
  }

  function remove(account: AccountDto): void {
    if (!window.confirm('مسح الحساب ' + account.code + ' — ' + account.nameAr + ' نهائيًا؟'))
      return;
    void run(() => api.deleteAccount(companyId, account.code), 'اتمسح الحساب ' + account.code);
  }

  return (
    <div className="panel">
      <h2>دليل الحسابات</h2>
      <Hint block>
        اضغط اسم أى حساب تشوف كشفه. «+ فرعى» بيضيف حساب تحت الحساب التجميعى بالكود اللى عليه الدور.
        الحساب اللى عليه حركات مايتمسحش — يتوقف بس، وحركاته القديمة فاضلة فى التقارير.
      </Hint>
      <div className="form-row">
        <label className="grow">
          بحث بالكود أو الاسم
          <input value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <label className="option">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />
          <span>إظهار الموقوف</span>
        </label>
      </div>

      {failure && <p className="error">{failure}</p>}
      {notice && <p className="sub">{notice}</p>}

      {adding && (
        <form className="form-row" onSubmit={submitAdd} noValidate>
          <label className="grow">
            اسم الحساب الجديد تحت {adding.parentCode}
            <input
              autoFocus
              value={adding.nameAr}
              onChange={(event) => setAdding({ ...adding, nameAr: event.target.value })}
              maxLength={200}
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy}>
              إضافة
            </button>
            <button type="button" onClick={() => setAdding(null)}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {renaming && (
        <form className="form-row" onSubmit={submitRename} noValidate>
          <label className="grow">
            الاسم الجديد للحساب {renaming.code}
            <input
              autoFocus
              value={renaming.nameAr}
              onChange={(event) => setRenaming({ ...renaming, nameAr: event.target.value })}
              maxLength={200}
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy}>
              حفظ
            </button>
            <button type="button" onClick={() => setRenaming(null)}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>الكود</th>
              <th>الحساب</th>
              <th>النوع</th>
              <th>الحالة</th>
              {canManage && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((account) => {
              const active = account.status === 'active';
              return (
                <tr key={account.id}>
                  <td className="mono">{account.code}</td>
                  <td style={{ paddingInlineStart: 8 + (depthOf.get(account.code) ?? 0) * 18 }}>
                    {account.isPostable ? (
                      <button
                        type="button"
                        className="link"
                        onClick={() => onOpenLedger(account.code)}
                      >
                        {account.nameAr}
                      </button>
                    ) : (
                      <strong>{account.nameAr}</strong>
                    )}
                  </td>
                  <td>{account.isPostable ? 'فرعى' : 'تجميعى'}</td>
                  <td>{active ? 'نشط' : <span className="pill bad">موقوف</span>}</td>
                  {canManage && (
                    <td>
                      <div className="panel-actions">
                        {!account.isPostable && active && (
                          <button
                            type="button"
                            className="ghost"
                            disabled={busy}
                            onClick={() => {
                              setRenaming(null);
                              setAdding({ parentCode: account.code, nameAr: '' });
                            }}
                          >
                            + فرعى
                          </button>
                        )}
                        <button
                          type="button"
                          className="ghost"
                          disabled={busy}
                          onClick={() => {
                            setAdding(null);
                            setRenaming({ code: account.code, nameAr: account.nameAr });
                          }}
                        >
                          تعديل
                        </button>
                        {account.isPostable && (
                          <button
                            type="button"
                            className="ghost"
                            disabled={busy}
                            onClick={() =>
                              void run(
                                () => api.setAccountActive(companyId, account.code, !active),
                                (active ? 'اتوقف' : 'اتفعّل') + ' الحساب ' + account.code,
                              )
                            }
                          >
                            {active ? 'إيقاف' : 'تفعيل'}
                          </button>
                        )}
                        {account.isPostable && (
                          <button
                            type="button"
                            className="ghost"
                            disabled={busy}
                            onClick={() => remove(account)}
                          >
                            مسح
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
